import React, {useEffect, useMemo, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircleCheck, faReceipt, faSpinner} from "@fortawesome/free-solid-svg-icons";
import {useUserLearning} from "../store/user";
import LearnerShell from "../components/learner/LearnerShell";
import TabStrip from "../components/learner/TabStrip";
import Paginator from "../components/learner/Paginator";
import EmptyState from "../components/learner/EmptyState";
import Toast from "../components/Toast";
import {money, shortDate} from "../lib/format";

const LIMIT = 10;
const TABS = [
  {id: "all", label: "All"},
  {id: "pending", label: "Pending"},
  {id: "success", label: "Successful"},
  {id: "failed", label: "Failed"},
];

const statusOf = (purchase) => String(purchase?.status || "").toUpperCase();

const TONES = {
  SUCCESS: "bg-emerald-50 text-emerald-700",
  COMPLETED: "bg-emerald-50 text-emerald-700",
  PAID: "bg-emerald-50 text-emerald-700",
  VERIFIED: "bg-emerald-50 text-emerald-700",
  PENDING: "bg-amber-50 text-amber-700",
  FAILED: "bg-rose-50 text-rose-700",
  CANCELLED: "bg-rose-50 text-rose-700",
};

const StatusPill = ({status}) => {
  const label = status || "UNKNOWN";
  return (
    <span
      className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${
        TONES[label.toUpperCase()] || "bg-slate-100 text-slate-600"
      }`}
    >
      {label}
    </span>
  );
};

/**
 * /learner/transactions — purchase history.
 *
 *   GET   /api/learners/purchases?page&limit
 *   GET   /api/learners/purchases/:reference
 *   GET   /api/learners/purchases/:reference/verify
 */
const LearnerTransactions = () => {
  const navigate = useNavigate();
  const {
    userPurchases,
    userPurchasesLoading,
    userPurchasesTotal,
    userPurchasesPages,
    userPurchasesError,
    fetchUserPurchases,
    fetchUserPurchase,
    userPurchaseDetail,
    userPurchaseDetailLoading,
    verifyUserPurchase,
  } = useUserLearning();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("all");
  const [openReference, setOpenReference] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const [toast, setToast] = useState(null);

  const page = Math.max(1, Number(searchParams.get("page")) || 1);
  const purchases = useMemo(() => (Array.isArray(userPurchases) ? userPurchases : []), [userPurchases]);

  useEffect(() => {
    fetchUserPurchases({page, limit: LIMIT});
  }, [fetchUserPurchases, page]);

  const filtered = useMemo(() => {
    if (tab === "pending") return purchases.filter((item) => statusOf(item) === "PENDING");
    if (tab === "success") {
      return purchases.filter((item) => ["SUCCESS", "COMPLETED", "PAID", "VERIFIED"].includes(statusOf(item)));
    }
    if (tab === "failed") return purchases.filter((item) => ["FAILED", "CANCELLED"].includes(statusOf(item)));
    return purchases;
  }, [tab, purchases]);

  const setPage = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next > 1) params.set("page", String(next));
    else params.delete("page");
    setSearchParams(params, {replace: true});
  };

  // GET /api/learners/purchases/:reference
  const toggleDetails = async (purchase) => {
    const reference = purchase?.reference;
    if (!reference) return;
    if (openReference === reference) {
      setOpenReference(null);
      return;
    }
    setOpenReference(reference);
    const result = await fetchUserPurchase(reference);
    if (!result.success) setToast(result.error || "Could not load purchase details");
  };

  // GET /api/learners/purchases/:reference/verify
  const verify = async (purchase) => {
    const reference = purchase?.reference;
    if (!reference) return;
    setVerifying(reference);
    const result = await verifyUserPurchase(reference);
    setVerifying(null);
    if (result.success) {
      setToast("Payment verified");
      fetchUserPurchases({page, limit: LIMIT});
    } else {
      setToast(result.error || "Could not verify payment");
    }
  };

  const tabs = TABS.map((entry) => ({
    ...entry,
    count:
      entry.id === "pending"
        ? purchases.filter((item) => statusOf(item) === "PENDING").length
        : entry.id === "success"
          ? purchases.filter((item) =>
              ["SUCCESS", "COMPLETED", "PAID", "VERIFIED"].includes(statusOf(item))
            ).length
          : entry.id === "failed"
            ? purchases.filter((item) => ["FAILED", "CANCELLED"].includes(statusOf(item))).length
            : purchases.length,
  }));

  const spentOnPage = purchases
    .filter((item) => ["SUCCESS", "COMPLETED", "PAID", "VERIFIED"].includes(statusOf(item)))
    .reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

  const rowButton =
    "inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-60";

  return (
    <LearnerShell
      title="My purchases"
      subtitle={`${userPurchasesTotal} transaction${
        userPurchasesTotal === 1 ? "" : "s"
      } · ${money(spentOnPage)} successful on this page`}
      icon={faReceipt}
      actions={
        <button
          type="button"
          onClick={() => navigate("/content-library")}
          className="rounded-md border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100"
        >
          Open my library
        </button>
      }
    >
      {toast ? <Toast message={toast} onClose={() => setToast(null)} isSuccess /> : null}

      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <TabStrip tabs={tabs} active={tab} onChange={setTab} />

        <div className="p-4 sm:p-6">
          {userPurchasesError ? (
            <p className="m-0 mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
              {userPurchasesError}
            </p>
          ) : null}

          {userPurchasesLoading && purchases.length === 0 ? (
            <div className="space-y-3">
              {[0, 1, 2].map((index) => (
                <div key={index} className="h-20 animate-pulse rounded-xl border border-slate-200 bg-white" />
              ))}
            </div>
          ) : filtered.length > 0 ? (
            <div className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200">
              {filtered.map((purchase) => {
                const status = statusOf(purchase);
                return (
                  <div key={purchase.id || purchase.reference}>
                    <div className="flex flex-wrap items-center gap-4 p-4">
                      <div className="min-w-0 flex-1">
                        <p className="m-0 truncate text-sm font-semibold text-slate-900">
                          {purchase.description || purchase.type || "Purchase"}
                        </p>
                        <p className="m-0 mt-0.5 truncate text-xs text-slate-500">
                          {[purchase.type, purchase.reference, shortDate(purchase.paidAt || purchase.createdAt)]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      </div>
                      <span className="text-sm font-semibold text-slate-900">{money(purchase.amount ?? 0)}</span>
                      <StatusPill status={status} />
                      {status === "PENDING" && purchase.reference ? (
                        <button
                          type="button"
                          className={rowButton}
                          disabled={verifying === purchase.reference}
                          onClick={() => verify(purchase)}
                        >
                          <FontAwesomeIcon
                            icon={verifying === purchase.reference ? faSpinner : faCircleCheck}
                            spin={verifying === purchase.reference}
                          />
                          Verify
                        </button>
                      ) : null}
                      {purchase.reference ? (
                        <button type="button" className={rowButton} onClick={() => toggleDetails(purchase)}>
                          {openReference === purchase.reference ? "Hide" : "Details"}
                        </button>
                      ) : null}
                    </div>

                    {openReference === purchase.reference ? (
                      <div className="border-t border-slate-100 bg-slate-50 px-4 py-3">
                        {userPurchaseDetailLoading ? (
                          <p className="m-0 text-xs text-slate-500">Loading details…</p>
                        ) : (
                          <dl className="m-0 grid gap-x-6 gap-y-1 text-xs text-slate-600 sm:grid-cols-3">
                            <div className="flex gap-2">
                              <dt className="font-semibold text-slate-500">Type</dt>
                              <dd className="m-0 truncate">{userPurchaseDetail?.type || "—"}</dd>
                            </div>
                            <div className="flex gap-2">
                              <dt className="font-semibold text-slate-500">Status</dt>
                              <dd className="m-0 truncate">{userPurchaseDetail?.status || "—"}</dd>
                            </div>
                            <div className="flex gap-2">
                              <dt className="font-semibold text-slate-500">Amount</dt>
                              <dd className="m-0 truncate">{money(userPurchaseDetail?.amount ?? 0)}</dd>
                            </div>
                            <div className="flex gap-2">
                              <dt className="font-semibold text-slate-500">Item</dt>
                              <dd className="m-0 truncate">{userPurchaseDetail?.itemId || "—"}</dd>
                            </div>
                            <div className="flex gap-2">
                              <dt className="font-semibold text-slate-500">Paid</dt>
                              <dd className="m-0 truncate">{shortDate(userPurchaseDetail?.paidAt) || "—"}</dd>
                            </div>
                            <div className="flex gap-2">
                              <dt className="font-semibold text-slate-500">Created</dt>
                              <dd className="m-0 truncate">{shortDate(userPurchaseDetail?.createdAt) || "—"}</dd>
                            </div>
                          </dl>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState
              icon={faReceipt}
              title={tab === "all" ? "No purchases yet" : "Nothing in this view"}
              hint="Course, book and journal purchases will be listed here."
              action={
                <button
                  type="button"
                  onClick={() => navigate("/books")}
                  className="rounded-md bg-blue-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-800"
                >
                  Browse books
                </button>
              }
            />
          )}

          <Paginator page={page} totalPages={userPurchasesPages} onChange={setPage} />
        </div>
      </section>
    </LearnerShell>
  );
};

export default LearnerTransactions;
